<script setup>
import { computed, reactive, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import bulkCampaignsAPI from 'dashboard/api/bulkCampaigns';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const alert = useAlert;

const uiFlags = computed(() => store.getters['bulkCampaigns/getUIFlags']);
const isCreating = computed(() => uiFlags.value.isCreating);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const isValidatingWhatsApp = ref(false);

const form = reactive({
  name: '',
  inbox_id: '',
  content: '',
  scheduled_at: '',
  batch_size: 6,
  batch_interval_minutes: 3,
  delay_between_messages_min: 45,
  delay_between_messages_max: 90,
  contacts: [
    { validationId: crypto.randomUUID(), phone: '', name: '', whatsappStatus: null }
  ],
  send_now: false,
});

const formatBrazilianPhone = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 8) {
    // 8 dígitos: fixo sem DDD (ex: 35917487) — assume DDD local (35)
    return `5535${digits}`;
  }
  if (digits.length === 10) {
    const third = digits[2];
    if (['9', '8', '1'].includes(third)) {
      // DDD + móvel sem 9 (ex: 3599380710 → 5535999380710)
      return `55${digits.slice(0, 2)}9${digits.slice(2)}`;
    }
    // DDD + fixo (ex: 3535911195 → 553535911195)
    return `55${digits}`;
  }
  if (digits.length === 11) {
    // DDD + 9 + móvel (ex: 35997412458 → 5535997412458)
    return `55${digits}`;
  }
  if (digits.length === 12 || digits.length === 13) {
    return digits;
  }
  return digits;
};

const addContact = () => form.contacts.unshift({
  validationId: crypto.randomUUID(),
  phone: '',
  name: '',
  whatsappStatus: null,
});
const removeContact = (index) => {
  if (form.contacts.length > 1) form.contacts.splice(index, 1);
};

const parseCSVLine = (line, delimiter) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
};

const handleCSVImport = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    const text = e.target.result;
    const lines = text.trim().split('\n');
    if (lines.length < 2) return;
    const headerLine = lines[0];
    const delimiter = headerLine.includes(';') ? ';' : ',';
    const headers = headerLine.toLowerCase().split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
    const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('telefone'));
    const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('nome'));
    if (phoneIdx === -1) return;
    const importedContacts = lines.slice(1).map(line => {
      const cols = parseCSVLine(line, delimiter);
      return {
        validationId: crypto.randomUUID(),
        phone: formatBrazilianPhone((cols[phoneIdx] || '').replace(/\D/g, '')),
        name: nameIdx >= 0 ? (cols[nameIdx] || '').replace(/^"|"$/g, '') : '',
        whatsappStatus: null
      };
    }).filter(c => c.phone);
    const phones = new Set();
    form.contacts = importedContacts.filter(contact => {
      if (phones.has(contact.phone)) return false;
      phones.add(contact.phone);
      return true;
    });
  };
  reader.readAsText(file);
  event.target.value = '';
};

const validateWhatsAppNumbers = async () => {
  const contacts = form.contacts.filter(contact => contact.phone.match(/^\d{12,13}$/));
  if (!contacts.length) {
    alert(t('BULK_CAMPAIGNS.ERRORS.AT_LEAST_ONE_CONTACT'));
    return;
  }

  isValidatingWhatsApp.value = true;
  contacts.forEach(contact => { contact.whatsappStatus = 'checking'; });
  try {
    const { data } = await bulkCampaignsAPI.checkWhatsApp(
      contacts.map(contact => ({
        validation_id: contact.validationId,
        phone: formatBrazilianPhone(contact.phone),
      }))
    );
    const results = new Map(data.results.map(result => [result.validation_id, result]));
    contacts.forEach(contact => {
      const result = results.get(contact.validationId);
      contact.whatsappStatus = result?.exists ? 'valid' : 'invalid';
    });
  } catch (error) {
    contacts.forEach(contact => { contact.whatsappStatus = 'error'; });
    alert(error.response?.data?.error || 'Não foi possível validar os números no WhatsApp.');
  } finally {
    isValidatingWhatsApp.value = false;
  }
};

const validContacts = computed(() => form.contacts.filter(c => c.phone.match(/^\d{12,13}$/)));
const totalContacts = computed(() => validContacts.value.length);
const totalBatches = computed(() => totalContacts.value === 0 ? 0 : Math.ceil(totalContacts.value / form.batch_size));
const estimatedMinutes = computed(() => totalBatches.value * form.batch_interval_minutes);
const estimatedTime = computed(() => {
  const m = estimatedMinutes.value;
  if (m < 60) return t('BULK_CAMPAIGNS.TIME.MINUTES_SHORT', { count: m });
  return t('BULK_CAMPAIGNS.TIME.HOURS_MINUTES_SHORT', { hours: Math.floor(m / 60), minutes: m % 60 });
});

const batchPreview = computed(() => {
  const batches = [];
  const contacts = validContacts.value;
  const hasSchedule = !form.send_now && form.scheduled_at;
  const baseTime = hasSchedule ? new Date(form.scheduled_at).getTime() : null;
  for (let i = 0; i < totalBatches.value; i++) {
    const start = i * form.batch_size;
    const end = Math.min(start + form.batch_size, contacts.length);
    const count = end - start;
    const mins = i * form.batch_interval_minutes;
    const timeStr = baseTime
      ? new Date(baseTime + mins * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      : null;
    batches.push({ number: i + 1, time: timeStr, count });
  }
  return batches;
});

const validateForm = () => {
  if (!form.name.trim()) { alert(t('BULK_CAMPAIGNS.ERRORS.NAME_REQUIRED')); return false; }
  if (!form.content.trim()) { alert(t('BULK_CAMPAIGNS.ERRORS.MESSAGE_REQUIRED')); return false; }
  if (!form.inbox_id) { alert(t('BULK_CAMPAIGNS.ERRORS.INBOX_REQUIRED')); return false; }
  if (validContacts.value.length === 0) { alert(t('BULK_CAMPAIGNS.ERRORS.AT_LEAST_ONE_CONTACT')); return false; }
  
  if (form.batch_size < 1 || form.batch_size > 100) { alert(t('BULK_CAMPAIGNS.ERRORS.BATCH_SIZE_RANGE')); return false; }
  if (form.batch_interval_minutes < 1 || form.batch_interval_minutes > 60) { alert(t('BULK_CAMPAIGNS.ERRORS.INTERVAL_RANGE')); return false; }
  if (form.delay_between_messages_min >= form.delay_between_messages_max) { alert(t('BULK_CAMPAIGNS.ERRORS.DELAY_MIN_MAX')); return false; }
  
  // Validate that scheduled_at is in the future (considering local timezone)
  if (form.scheduled_at && !form.send_now) {
    const scheduledDate = new Date(form.scheduled_at);
    if (Number.isNaN(scheduledDate.getTime())) {
      alert(t('BULK_CAMPAIGNS.ERRORS.INVALID_DATE_FORMAT'));
      return false;
    }
    if (scheduledDate <= new Date()) {
      alert(t('BULK_CAMPAIGNS.ERRORS.SCHEDULE_IN_FUTURE'));
      return false;
    }
  }
  
  return true;
};

const handleSubmit = async () => {
    if (!validateForm()) return;

    // Convert scheduled_at to ISO string UTC if filled
    let scheduledAt = null;
    if (form.scheduled_at && !form.send_now) {
      // datetime-local returns "YYYY-MM-DDTHH:MM" (local time)
      // Convert to ISO string UTC
      const localDate = new Date(form.scheduled_at);
      scheduledAt = localDate.toISOString(); // e.g. "2026-08-23T13:29:00.000Z" (UTC)
    }

    const payload = {
      bulk_campaign: {
        name: form.name.trim(),
        content: form.content.trim(),
        inbox_id: Number(form.inbox_id),
        scheduled_at: form.send_now ? null : scheduledAt,
        batch_size: form.batch_size,
        batch_interval_minutes: form.batch_interval_minutes,
        delay_between_messages_min: form.delay_between_messages_min,
        delay_between_messages_max: form.delay_between_messages_max,
        contacts: validContacts.value.map(c => ({ phone: c.phone, name: c.name })),
      }
    };

    try {
      const result = await store.dispatch('bulkCampaigns/create', { payload });
      alert(t('BULK_CAMPAIGNS.CREATED_SUCCESS'));
      if (form.send_now && result?.id) {
        await store.dispatch('bulkCampaigns/sendNow', { id: result.id });
        alert(t('BULK_CAMPAIGNS.SENT_NOW_SUCCESS'));
      }
      router.push({ name: 'bulk_campaigns_main' });
    } catch (e) {
      // Show actual error message from server if available
      const errorMessage = e?.response?.data?.error || e?.message || t('BULK_CAMPAIGNS.ERRORS.CREATE_FAILED');
      alert(errorMessage);
    }
  };

const goBack = () => router.back();

onMounted(() => {
  if (store.getters['inboxes/getInboxes'].length === 0) {
    store.dispatch('inboxes/get');
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-divider">
      <div class="w-full max-w-5xl mx-auto h-20 flex items-center justify-between gap-4">
        <button @click="goBack" class="p-2 rounded-lg text-n-text-light hover:bg-n-surface-2 transition-colors" :title="t('COMMON.BACK')">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div class="flex-1">
          <h1 class="text-heading-1 text-n-slate-12 truncate">{{ t('BULK_CAMPAIGNS.NEW_TITLE') }}</h1>
          <p class="text-sm text-n-text-light dark:text-n-text-muted mt-1">{{ t('BULK_CAMPAIGNS.SUBTITLE') }}</p>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6 space-y-6">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.NAME') }}</label>
            <input v-model="form.name" type="text" :placeholder="t('BULK_CAMPAIGNS.NAME_PLACEHOLDER')" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.INBOX') }}</label>
            <select v-model="form.inbox_id" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500">
              <option value="">{{ t('BULK_CAMPAIGNS.INBOX_PLACEHOLDER') }}</option>
              <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
            </select>
          </div>

          <fieldset class="bg-n-surface-2/50 rounded-xl p-4 border border-n-divider">
            <legend class="text-sm font-semibold text-n-slate-12 mb-1 px-1 flex items-center gap-2">
              {{ t('BULK_CAMPAIGNS.MESSAGE') }}
              <span class="text-xs font-normal text-n-slate-9">{{ t('BULK_CAMPAIGNS.VARIABLES') }}</span>
            </legend>
            <WootMessageEditor
              v-model="form.content"
              :placeholder="t('BULK_CAMPAIGNS.MESSAGE_PLACEHOLDER')"
              :show-emoji-picker="true"
              :show-attachments="false"
              :enable-variables="true"
              :variables="{ nome: '{{nome}}', telefone: '{{telefone}}', data: '{{data}}', hora: '{{hora}}', campanha: '{{campanha}}' }"
              class="w-full"
            />
          </fieldset>

          <fieldset class="bg-n-surface-2/50 rounded-xl p-4 border border-n-divider">
            <legend class="text-sm font-semibold text-n-slate-12 mb-3 px-1">{{ t('BULK_CAMPAIGNS.CONTACTS') }}</legend>

            <div class="flex items-center gap-3 mb-4">
              <button type="button" @click="addContact" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                {{ t('BULK_CAMPAIGNS.ADD_CONTACT') }}
              </button>
              <input type="file" accept=".csv" @change="handleCSVImport" class="hidden" ref="csvInput" />
               <button type="button" @click="$refs.csvInput.click()" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg shadow-sm transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                 {{ t('BULK_CAMPAIGNS.IMPORT_CSV') }}
               </button>
               <button type="button" @click="validateWhatsAppNumbers" :disabled="isValidatingWhatsApp" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors">
                 <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                 {{ isValidatingWhatsApp ? 'Validando...' : 'Validar WhatsApp' }}
               </button>
              <span class="text-xs text-n-slate-9">{{ t('BULK_CAMPAIGNS.CSV_HELP') }}</span>
            </div>

            <div class="space-y-2 max-h-64 overflow-y-auto">
              <div v-for="(contact, index) in form.contacts" :key="index" class="flex items-center gap-2">
                <span class="text-xs font-mono text-n-slate-9 w-6 text-center shrink-0">{{ index + 1 }}</span>
                 <input v-model="contact.phone" type="text" :placeholder="t('BULK_CAMPAIGNS.PHONE_PLACEHOLDER')" class="flex-1 px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500 text-sm placeholder:text-n-slate-8" />
                 <input v-model="contact.name" type="text" :placeholder="t('BULK_CAMPAIGNS.NAME_PLACEHOLDER_CONTACT')" class="flex-1 px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500 text-sm placeholder:text-n-slate-8" />
                 <span class="w-6 text-center text-base" :title="contact.whatsappStatus === 'valid' ? 'Possui WhatsApp' : contact.whatsappStatus === 'invalid' ? 'Sem WhatsApp' : ''">
                   <span v-if="contact.whatsappStatus === 'checking'" class="text-amber-600">...</span>
                   <span v-else-if="contact.whatsappStatus === 'valid'" class="text-green-600">✓</span>
                   <span v-else-if="contact.whatsappStatus === 'invalid' || contact.whatsappStatus === 'error'" class="text-red-600">×</span>
                 </span>
                <button type="button" @click="removeContact(index)" class="p-2 text-n-slate-9 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" :disabled="form.contacts.length <= 1" :title="t('COMMON.REMOVE')">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div class="text-sm text-n-text-light dark:text-n-text-muted mt-2">{{ t('BULK_CAMPAIGNS.CONTACTS_ADDED', { count: totalContacts }) }}</div>
          </fieldset>

          <fieldset class="bg-n-surface-2/50 rounded-xl p-4 border border-n-divider">
            <legend class="text-sm font-semibold text-n-slate-12 mb-3 px-1">{{ t('BULK_CAMPAIGNS.BATCH_CONFIG') }}</legend>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.BATCH_SIZE') }}</label>
                <input v-model.number="form.batch_size" type="number" min="1" max="100" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500" />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.BATCH_INTERVAL') }}</label>
                <input v-model.number="form.batch_interval_minutes" type="number" min="1" max="60" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500" />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.DELAY_MIN') }}</label>
                <input v-model.number="form.delay_between_messages_min" type="number" min="45" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500" />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ t('BULK_CAMPAIGNS.DELAY_MAX') }}</label>
                <input v-model.number="form.delay_between_messages_max" type="number" min="90" class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500" />
              </div>
            </div>
          </fieldset>

          <fieldset class="bg-n-surface-2/50 rounded-xl p-4 border border-n-divider">
            <legend class="text-sm font-semibold text-n-slate-12 mb-3 px-1">{{ t('BULK_CAMPAIGNS.SCHEDULE_AT') }}</legend>
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="form.send_now" type="checkbox" class="w-4 h-4 text-n-primary-600 border-n-divider rounded focus:ring-n-primary-500" />
                <span class="text-sm font-medium text-n-slate-12">{{ t('BULK_CAMPAIGNS.SEND_NOW') }}</span>
              </label>
              <input v-model="form.scheduled_at" type="datetime-local" :disabled="form.send_now" class="flex-1 px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500 disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
          </fieldset>

          <fieldset class="bg-n-surface-2/50 rounded-xl p-4 border border-n-divider">
            <legend class="text-sm font-semibold text-n-slate-12 mb-3 px-1">{{ t('BULK_CAMPAIGNS.PREVIEW') }}</legend>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between"><span class="text-n-slate-11">{{ t('BULK_CAMPAIGNS.TOTAL_CONTACTS', { count: totalContacts }) }}</span></div>
              <div class="flex justify-between"><span class="text-n-slate-11">{{ t('BULK_CAMPAIGNS.TOTAL_BATCHES', { count: totalBatches }) }}</span></div>
              <div class="flex justify-between"><span class="text-n-slate-11">{{ t('BULK_CAMPAIGNS.ESTIMATED_TIME', { time: estimatedTime }) }}</span></div>
            </div>
            <div v-if="totalBatches > 0" class="mt-3 max-h-32 overflow-y-auto space-y-1 border border-n-divider rounded-lg p-2 bg-white dark:bg-n-surface-1">
              <div v-for="b in batchPreview" :key="b.number" class="flex justify-between text-xs py-0.5">
                <span class="font-medium text-n-slate-12">{{ t('BULK_CAMPAIGNS.BATCH_PREVIEW_SHORT', { number: b.number, count: b.count }) }}</span>
                <span v-if="b.time" class="text-woot-600 font-mono font-semibold">{{ b.time }}</span>
                <span v-else class="text-n-slate-9 italic">—</span>
              </div>
            </div>
            <div v-if="totalBatches === 0" class="text-xs text-n-slate-9 text-center py-4">{{ t('BULK_CAMPAIGNS.EMPTY') }}</div>
          </fieldset>

          <div class="flex justify-end gap-3 border-t border-n-divider pt-4">
            <button type="button" @click="goBack" class="px-4 py-2 text-sm font-medium text-n-slate-11 border border-n-divider rounded-lg hover:bg-n-surface-2 transition-colors">{{ t('BULK_CAMPAIGNS.CANCEL') }}</button>
            <button type="submit" :disabled="isCreating" class="px-4 py-2 text-sm font-medium text-white bg-woot-600 hover:bg-woot-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {{ isCreating ? t('COMMON.SAVING') : (form.send_now ? t('BULK_CAMPAIGNS.SEND_NOW') : t('BULK_CAMPAIGNS.SCHEDULE')) }}
            </button>
          </div>
        </form>
      </div>
    </main>
  </section>
</template>

<style scoped>
input[type="text"] { margin-bottom: 0 !important; }
</style>
